<script setup lang="ts">
import { personAdd } from "ionicons/icons";

import {
  IonPage,
  IonLabel,
  IonItem,
  IonNote,
  IonFooter,
  IonList,
  IonContent,
  IonInput,
  IonCheckbox,
  IonButton,
  IonLoading,
  IonIcon,
  IonTitle,
  IonButtons,
  IonHeader,
  IonToolbar,
  IonBackButton,
  useIonRouter,
} from "@ionic/vue";
import { useForm } from "../..";
import { addUser } from "@/api/users";
import { computed, reactive } from "vue";
import { useToasters } from "@/composition/toasters";
import { useWindowWidth } from "@/composition/window";
import { event as gtag_event } from "vue-gtag";

const { isMobile } = useWindowWidth();
const router = useIonRouter();
const form = useForm();

const state = reactive({
  loading: false,
  acceptedTerms: false,
  errors: {
    email: [],
    name: [],
    password: [],
  },
});

const toasters = useToasters();

const emptyFieldsPresent = computed(
  () =>
    form.user.name === "" ||
    form.user.email === "" ||
    form.user.password === "" ||
    form.user.confirm_password === "" ||
    !state.acceptedTerms
);

const createUser = () => {
  state.loading = true;
  state.errors = {
    email: [],
    name: [],
    password: [],
  };
  addUser(form.user)
    .then((response) => {
      state.loading = false;

      if (response.status === 201) {
        gtag_event("sign_up", {
          category: "check-in app",
          label: "New registration",
        });

        router.push("/onboarding/verify-email");
      }
    })
    .catch((err) => {
      if (Array.isArray(err.response.data)) {
        toasters.addError(err.response.data[0]);
      } else {
        state.errors = { ...state.errors, ...err.response.data };
      }

      state.loading = false;
    });
};
</script>
<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button
            color="white"
            default-href="/onboarding/introduction"
          ></ion-back-button>
        </ion-buttons>
        <ion-title>New account</ion-title>

        <!-- <ion-progress-bar :value="0.25"></ion-progress-bar> -->
      </ion-toolbar>
    </ion-header>

    <ion-content v-if="isMobile">
      <div class="container">
        <h1>Get started with Beamian</h1>
        <p>Create an account and you’re good to go!</p>
      </div>
      <ion-list>
        <ion-item :class="{ 'ion-invalid': state.errors.name.length > 0 }">
          <ion-label position="stacked">Name</ion-label>
          <ion-input
            v-model="form.user.name"
            placeholder="John Doe"
            type="text"
          ></ion-input>
          <ion-note
            slot="error"
            v-for="(message, i) in state.errors.name"
            :key="`name-error-${i}`"
          >
            {{ message }}
          </ion-note>
        </ion-item>
        <ion-item :class="{ 'ion-invalid': state.errors.email.length > 0 }">
          <ion-label position="stacked"> Email</ion-label>
          <ion-input
            type="email"
            v-model="form.user.email"
            placeholder="john.doe@gmail.com"
          >
          </ion-input>
          <ion-note
            slot="error"
            v-for="(message, i) in state.errors.email"
            :key="`email-error-${i}`"
          >
            {{ message }}
          </ion-note>
        </ion-item>
        <ion-item :class="{ 'ion-invalid': state.errors.password.length > 0 }">
          <ion-label position="stacked"> Password</ion-label>
          <ion-input
            type="password"
            v-model="form.user.password"
            placeholder="********"
          >
          </ion-input>
          <ion-note
            slot="error"
            v-for="(message, i) in state.errors.password"
            :key="`password-error-${i}`"
          >
            {{ message }}
          </ion-note>
        </ion-item>
        <ion-item
          :class="{
            'ion-invalid':
              form.user.password !== '' &&
              form.user.password !== form.user.confirm_password,
          }"
        >
          <ion-label position="stacked"> Confirm password</ion-label>
          <ion-input
            type="password"
            v-model="form.user.confirm_password"
            placeholder="********"
          >
          </ion-input>
          <ion-note slot="error"> Passwords do not match </ion-note>
        </ion-item>
        <ion-item style="padding: 1.5rem 0; --border-color: #fff">
          <ion-checkbox v-model="state.acceptedTerms" style="margin-right: 5px;"> </ion-checkbox>
          <ion-note class="terms" color="dark">
            I confirm that I have read and agree to
            <b><a href="https://beamian.com/legal-policies/" target="_blank">beamian terms and conditions</a></b>.
          </ion-note>
        </ion-item>
        <ion-item style="--border-color: #fff">
          <ion-checkbox v-model="form.user.emails_opt_in" style="margin-right: 5px;"> </ion-checkbox>
          <ion-note class="terms" color="dark">
            I want to receive emails about new features and updated app
            versions.
          </ion-note>
        </ion-item>
      </ion-list>
    </ion-content>

    <div class="colored-bg full-height" v-else>
      <div class="container">
        <div>
          <h1>Get started with Beamian</h1>
          <p>Create an account and you’re good to go!</p>
        </div>
        <ion-list>
          <ion-item :class="{ 'ion-invalid': state.errors.name.length > 0 }">
            <ion-label position="stacked">Name</ion-label>
            <ion-input
              v-model="form.user.name"
              placeholder="John Doe"
              type="text"
            ></ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.name"
              :key="`name-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.email.length > 0 }">
            <ion-label position="stacked"> Email</ion-label>
            <ion-input
              type="email"
              v-model="form.user.email"
              placeholder="john.doe@gmail.com"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.email"
              :key="`email-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{ 'ion-invalid': state.errors.password.length > 0 }"
          >
            <ion-label position="stacked"> Password</ion-label>
            <ion-input
              type="password"
              v-model="form.user.password"
              placeholder="********"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.password"
              :key="`password-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{
              'ion-invalid':
                form.user.password !== '' &&
                form.user.password !== form.user.confirm_password,
            }"
          >
            <ion-label position="stacked"> Confirm password</ion-label>
            <ion-input
              type="password"
              v-model="form.user.confirm_password"
              placeholder="********"
            >
            </ion-input>
            <ion-note slot="error"> Passwords do not match </ion-note>
          </ion-item>

          <ion-item style="padding-top: 1rem; --border-color: #fff">
            <ion-checkbox v-model="state.acceptedTerms" style="margin-right: 5px;"> </ion-checkbox>
            <ion-note class="terms" color="dark">
              I confirm that I have read and agree to 
              <b><a href="https://beamian.com/legal-policies/" target="_blank">beamian terms and conditions</a></b>.
            </ion-note>
          </ion-item>
          <ion-item style="--border-color: #fff">
            <ion-checkbox v-model="form.user.emails_opt_in" style="margin-right: 5px;"> </ion-checkbox>
            <ion-note class="terms" color="dark">
              I want to receive emails about new features and updated app
              versions.
            </ion-note>
          </ion-item>
        </ion-list>
        <div style="width: 300px; margin: 50px auto 30px auto">
          <ion-button
            expand="block"
            color="success"
            @click="createUser()"
            :disabled="emptyFieldsPresent"
          >
            <ion-icon slot="start" :icon="personAdd"></ion-icon>
            Continue
          </ion-button>
        </div>
      </div>
    </div>

    <ion-footer v-if="isMobile">
      <div class="container">
        <section>
          <ion-button
            expand="block"
            color="success"
            @click="createUser()"
            :disabled="emptyFieldsPresent"
          >
            <ion-icon slot="start" :icon="personAdd"></ion-icon>
            Continue
          </ion-button>
        </section>
      </div>
    </ion-footer>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>

<style scoped lang="scss">
ion-note.terms {
  width: 100%;
  font-size: 14px;
}

@media (min-width: 768px) {
  ion-note.terms {
    align-self: center;
    margin-left: 7px;
  }
}
</style>
